import React, { useCallback, useMemo } from 'react'
import classnames from 'classnames'
import { intl } from 'lang/intl'

import { WebinarInfoSidebarComponent } from '../../../../components/WebinarInfoSidebarComponent/WebinarInfoSidebarComponent'
import HandoutsFeature from '../../../HandoutsFeature'
import { useWebinarColors } from '../../../../hooks/useWebinarColors'
import DigitalEventCard from '../../../WebinarSidebarFeature/components/WebinarSidebarComponent/DigitalEventCard'
import SessionsDrawer from '../../../WebinarSidebarFeature/components/WebinarSidebarComponent/SessionsDrawer/SessionsDrawer'
import { WebinarInfoAudienceSidebarComponent } from '../WebinarInfoAudienceSidebarComponent/WebinarInfoAudienceSidebarComponent'
import {
  WebinarPanelComponent,
  WebinarSidebarSecondaryPanelComponent,
} from '../../../WebinarSidebarFeature/components'
import { PollsFeature } from '../../..'
import useFeatures from '../../../../hooks/useFeatures'
import { Chat } from '../../../../../Chat/components/Chat/Chat'
import { ActionCard } from '../../../../../ActionCards/components/ActionCard'
import { useWebinarFormatQuery } from '../../../../hooks/useWebinarFormatQuery'
import { useLiveDataQuery } from '../../../../../global/blocs/useLiveData'

import Header from './Header'

import './WebinarAudienceSidebarComponent.scss'

interface WebinarAudienceSidebarProps {
  liveId: string
  token: string
  localParticipantName: string
  isSecondaryPanelVisible: boolean
  toggleSecondaryPanel: (panel: null | 'sessions') => void
  onAskQuestion: () => void
  togglePollOverlay: () => void
  isInGearMode: boolean
  secondaryPanelContentType: null | 'sessions'
}

const WebinarAudienceSidebarComponent = ({
  token,
  isSecondaryPanelVisible,
  toggleSecondaryPanel,
  onAskQuestion,
  togglePollOverlay,
  isInGearMode,
  secondaryPanelContentType,
}: WebinarAudienceSidebarProps) => {
  const webinarColors = useWebinarColors()
  const accentColor = webinarColors.accent.color
  const tabColor = webinarColors.tabs.color
  const textStyle = { color: webinarColors.tabs.highContrast.color }
  const iconColor = webinarColors.sidebar.contrast.transparent('25%')

  const { data: liveData } = useLiveDataQuery()

  const liveSeriesId = Number(liveData?.live_series_id) || undefined
  const { data: series } = useWebinarFormatQuery(
    Number(liveData?.live_series_id),
    liveData?.live_series_token
  )

  const { HasWebinarActionCardsEnabled } = useFeatures()

  const styles = useMemo(
    () => ({
      inputStyle: {
        color: webinarColors.tabs.highContrast.color,
        backgroundColor:
          webinarColors.sidebar.contrast.color === '#000000'
            ? 'rgba(0, 0, 0, 0.05)'
            : 'rgba(0, 0, 0, 0.15)',
      },
      buttonStyle: { backgroundColor: webinarColors.accent.color },
      secondaryButtonStyle: {
        backgroundColor: webinarColors.sidebar.contrast.transparent('20%'),
        color: webinarColors.accent.color,
      },
      progressBarStyles: {
        strokeColor: webinarColors.accent.color,
        trailColor: webinarColors.sidebar.color,
      },
      radioButtonStyles: {
        passiveColor: webinarColors.tabs.contrast.color,
        activeColor: webinarColors.accent.color,
      },
    }),
    [webinarColors]
  )

  const renderPollUnitWrapper = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (pollProps: any) => {
      return (
        <WebinarPanelComponent
          style={{
            color: webinarColors.tabs.highContrast.color,
            backgroundColor: webinarColors.tabs.color,
          }}
          {...pollProps}
          title={
            pollProps.title && intl.formatMessage({ defaultMessage: 'Poll' })
          }
          icon={{
            ...pollProps.icon,
            color: webinarColors.sidebar.contrast.transparent('25%'),
          }}
        />
      )
    },
    [webinarColors]
  )

  const cssVars = {
    '--webinar-color-accent': webinarColors.accent.color,
    '--webinar-color-sidebar': webinarColors.sidebar.color,
    '--webinar-color-tabs': webinarColors.tabs.color,
    '--webinar-color-sidebar-text': webinarColors.sidebar.highContrast.color,
    '--webinar-color-sidebar-ctacontrast': webinarColors.sidebar.contrast.color,
    '--webinar-color-tabs-text': webinarColors.tabs.highContrast.color,
    '--webinar-color-tabs-ctacontrast': webinarColors.tabs.contrast.color,
    '--webinar-color-accent-text': webinarColors.accent.highContrast.color,
    '--webinar-color-accent-ctacontrast': webinarColors.accent.contrast.color,
    '--webinar-color-sidebarSecondaryText':
      webinarColors.tabs.highContrast.transparent('85%'),
    '--webinar-color-inputBox':
      webinarColors.sidebar.contrast.color === '#000000'
        ? 'rgba(0, 0, 0, 0.05)'
        : 'rgba(0, 0, 0, 0.15)',
  } as React.CSSProperties

  const isDigitalEvent = !!liveData?.live_series_id

  const audienceChatEnabled =
    liveData?.comment_mode === 'all' || liveData?.comment_mode === 'speakers'

  if (!liveData) return null

  return (
    <div className="webinar-audience-sidebar-component" style={cssVars}>
      {isDigitalEvent && (
        <Header
          token={liveData.live_series_token}
          liveSeriesId={liveSeriesId}
          toggleSecondaryPanel={toggleSecondaryPanel}
          secondaryPanelContentType={secondaryPanelContentType}
        />
      )}
      <div
        className={classnames('sidebar-main-panel-wrapper', {
          hidden: isSecondaryPanelVisible,
        })}
        style={{
          backgroundColor: webinarColors.sidebar.color,
          color: webinarColors.sidebar.highContrast.color,
        }}
      >
        <WebinarInfoAudienceSidebarComponent
          onAskQuestion={onAskQuestion}
          styles={styles}
          isMobileDevice={false}
        />
        {series &&
          series.sessionCount > 0 &&
          series.seriesFormat !== 'series-recurring' && (
            <DigitalEventCard
              title={series.name}
              thumbnailUrl={series.thumbnailUrl}
              numberOfSessions={series.sessionCount}
              onClick={() => toggleSecondaryPanel('sessions')}
            />
          )}

        {HasWebinarActionCardsEnabled && <ActionCard />}
        <PollsFeature
          styles={styles}
          iconColor={iconColor}
          accentColor={accentColor}
          objectToken={token}
          renderPollUnitWrapper={renderPollUnitWrapper}
          disableOverlayTrigger={isInGearMode}
          isPublicPollOnly
          canChangePoll={false}
          isVotingAllowed
          namespace="webinar-main-sidebar-polls"
          siblingFeatureNameSpace="webinar-secondary-sidebar-polls"
          togglePollOverlay={togglePollOverlay}
        />
        <HandoutsFeature
          namespace="webinar-sidebar-handouts"
          liveToken={token}
          style={{ ...textStyle, backgroundColor: tabColor }}
          styles={{ progressBarColor: accentColor }}
          iconColor={iconColor}
          mode="view"
        />
        {audienceChatEnabled ? <Chat /> : <WebinarInfoSidebarComponent />}
      </div>
      {isSecondaryPanelVisible && secondaryPanelContentType && liveSeriesId && (
        <div className="secondary-panel-wrapper">
          <WebinarSidebarSecondaryPanelComponent>
            {
              {
                sessions: (
                  <SessionsDrawer
                    seriesId={liveSeriesId}
                    seriesToken={liveData.live_series_token}
                    webinarId={Number(liveData.live_id)}
                  />
                ),
              }[secondaryPanelContentType]
            }
          </WebinarSidebarSecondaryPanelComponent>
        </div>
      )}
    </div>
  )
}

export { WebinarAudienceSidebarComponent }
